import React, { useCallback, useState, useEffect } from "react";

import { useStores } from "../../../hooks/useStores";
import { observer } from "mobx-react";

import { useTranslation } from "react-i18next";

import {
  Flex,
  Spacer,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useToast,
} from "@chakra-ui/react";
import { useDisclosure } from "@chakra-ui/react";

import { EmailModal } from "../../../components/EmailModal";
import { EmailTable } from "../../../components/EmailTable";
import { AddIcon } from "@chakra-ui/icons";
import { Email } from "../../../types";

export const TabEmail: React.FC = observer(() => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();
  const [email, setEmail] = useState<Email | null>();
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure();
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const toast = useToast();
  const cancelRef = React.useRef();

  useEffect(() => {
    if (clientsStore.customerData) {
      clientsStore.getEmail(clientsStore.customerData.id);
    }
  }, [clientsStore.customerData]);

  const handleSubmit = useCallback(
    async (data: Partial<Email>) => {
      if (!clientsStore.customerData) {
        return;
      }

      try {
        if (data.id) {
          await clientsStore.updateEmail(clientsStore.customerData.id, data);
        } else {
          await clientsStore.addEmail(clientsStore.customerData.id, data);
        }
        onModalClose();
        setEmail(null);
      } catch (err) {
        console.log(err);
      }
    },
    [clientsStore, onModalClose]
  );

  const handleConfirmDelete = useCallback(
    (email: Email) => {
      setEmail(email);
      onAlertOpen();
    },
    [onAlertOpen]
  );

  const handleDelete = useCallback(async () => {
    try {
      if (email) {
        await clientsStore.deleteEmail(email);
        toast({
          status: "success",
          title: t("common.actionExecutedSuccessfully"),
        });
        onAlertClose();
      }
    } catch (err) {
      toast({
        status: "error",
        title: t("common.error"),
      });
    }
  }, [clientsStore, email, onAlertClose, t, toast]);

  return (
    <Flex mb={"20px"} direction="column">
      <Flex>
        <Spacer />
        <IconButton
          w={8}
          h={8}
          p={1}
          mx={"auto"}
          my={"10px"}
          aria-label="Add email"
          variant="ghost"
          color="black"
          fontSize="20px"
          icon={<AddIcon />}
          onClick={onModalOpen}
        />
      </Flex>
      <EmailTable
        emails={clientsStore.customerEmails}
        onEdit={(data) => {
          setEmail(data);
          onModalOpen();
        }}
        onDelete={handleConfirmDelete}
      />
      <EmailModal
        email={email}
        isOpen={isModalOpen}
        onClose={() => {
          onModalClose();
          setEmail(null);
        }}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        motionPreset="slideInBottom"
        // @ts-ignore
        leastDestructiveRef={cancelRef}
        onClose={onAlertClose}
        isOpen={isAlertOpen}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("screens.client.emailAccordionDelete.title")}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t("screens.client.emailAccordionDelete.dialog")}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                // @ts-ignore
                ref={cancelRef}
                onClick={onAlertClose}
              >
                {t("common.cancel")}
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                {t("common.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
});
